import { createTheme } from '@mui/material/styles';

/**
 * El tema del sitio, igual al que ya estaba.
 *
 * Los colores y los ajustes por componente son los mismos: es la misma marca y la misma
 * pantalla, sólo cambia quién arma el HTML.
 */
const COLORS = {
  PRIMARY: '#e60000',

  // Variante mas clara del rojo de la marca, para TEXTO sobre fondo oscuro. El #e60000 puro
  // sobre #121212 da 3.89:1 y no llega al 4.5:1 que pide WCAG AA; el #ff4242 da ~5.2:1 y
  // sigue leyéndose como el rojo de CKING. Los botones contained conservan el original.
  PRIMARY_TEXT: '#ff4242',

  SECONDARY: '#f50057',
  BACKGROUND_DEFAULT: '#121212',
  BACKGROUND_PAPER: '#1e1e1e',
  PRIMARY_HOVER: 'rgba(230, 0, 0, 0.2)',
  PRIMARY_HOVER_DARK: 'rgba(230, 0, 0, 0.3)',
} as const;

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: COLORS.PRIMARY },
    secondary: { main: COLORS.SECONDARY },
    background: {
      default: COLORS.BACKGROUND_DEFAULT,
      paper: COLORS.BACKGROUND_PAPER,
    },
  },

  typography: {
    // Las dos familias se sirven desde el propio dominio (@fontsource). Antes las servía
    // next/font, que hacía lo mismo: nadie le pide las fuentes a Google desde el navegador
    // del visitante.
    fontFamily: 'var(--font-roboto)',
    // h1, h2 y h3 son los titulos de display del sitio: portada, secciones y ficha. Van con
    // `clamp` para que crezcan con la pantalla sin media queries, interlineado corto -a este
    // tamanio el default deja demasiado aire entre renglones- y el tracking cerrado, que es lo
    // que hace que una condensada grande se lea como un bloque y no como letras sueltas.
    //
    // h4, h5 y h6 quedan como estaban a proposito: son las que usan las tarjetas y el panel,
    // donde un titulo de display seria un titulo que no entra.
    h1: {
      fontFamily: 'var(--font-roboto-condensed)',
      fontWeight: 700,
      fontSize: 'clamp(2.75rem, 7vw, 5rem)',
      lineHeight: 0.95,
      letterSpacing: '-0.025em',
    },
    h2: {
      fontFamily: 'var(--font-roboto-condensed)',
      fontWeight: 700,
      fontSize: 'clamp(2rem, 4.5vw, 3.25rem)',
      lineHeight: 1,
      letterSpacing: '-0.02em',
    },
    h3: {
      fontFamily: 'var(--font-roboto-condensed)',
      fontWeight: 600,
      fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
      lineHeight: 1.05,
      letterSpacing: '-0.015em',
    },
    button: {
      fontFamily: 'var(--font-roboto-condensed)',
      fontWeight: 600,
      letterSpacing: '0.05em',
    },
  },

  components: {
    MuiButton: {
      styleOverrides: {
        // MUI 9 eliminó las slots *color (outlinedPrimary, textPrimary…). La forma nueva es la
        // versión funcional de `root`, condicionada por ownerState.
        root: ({ ownerState }) => ({
          borderRadius: 0,
          textTransform: 'none',
          padding: '10px 24px',
          ...(ownerState.variant === 'outlined' &&
            ownerState.color === 'primary' && {
              color: COLORS.PRIMARY_TEXT,
              borderColor: COLORS.PRIMARY_TEXT,
              '&:hover': {
                borderColor: COLORS.PRIMARY_TEXT,
                backgroundColor: COLORS.PRIMARY_HOVER,
              },
            }),
          ...(ownerState.variant === 'text' &&
            ownerState.color === 'primary' && {
              color: COLORS.PRIMARY_TEXT,
              '&:hover': { backgroundColor: COLORS.PRIMARY_HOVER },
            }),
        }),
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          backgroundColor: COLORS.BACKGROUND_PAPER,
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.color === 'primary' &&
            ownerState.variant !== 'outlined' && {
              backgroundColor: COLORS.PRIMARY,
            }),
          ...(ownerState.color === 'primary' &&
            ownerState.variant === 'outlined' && {
              borderColor: COLORS.PRIMARY_TEXT,
              color: COLORS.PRIMARY_TEXT,
            }),
        }),
      },
    },

    MuiPaginationItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': { backgroundColor: COLORS.PRIMARY_HOVER },
          '&.Mui-selected:hover': { backgroundColor: COLORS.PRIMARY_HOVER_DARK },
        },
      },
    },
  },
});

export default darkTheme;
